import React from 'react';
import addYears from 'date-fns/addYears';
import subYears from 'date-fns/subYears';
import formatDate from 'date-fns/format';
import subMonths from 'date-fns/subMonths';

import { ErrorBoundary } from '@benzinga/core-ui';
import type { CalendarDataI, ICalendarProps } from '@benzinga/calendars';
import { AnalystRatingsColumnsDef, Calendars } from '@benzinga/calendars';
import type { ShortInterest } from '@benzinga/quotes-manager';
import type { InsidersFiling } from '@benzinga/insider-trades-manager';
import type { Guidance, Earnings, Ratings, Dividend, CompanyEventDetails } from '@benzinga/calendar-manager';

type CalendarTabSlugs =
  | 'analyst-ratings'
  | 'events'
  | 'guidance'
  | 'dividends'
  | 'earnings'
  | 'short-interest'
  | 'insider-trades';

type CalendarInitialDataTypes =
  | Ratings
  | Guidance
  | Dividend
  | Earnings
  | ShortInterest
  | InsidersFiling
  | CompanyEventDetails;

const GuidanceCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.GuidanceCalendar,
  })),
);

const AnalystRatingCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.AnalystRatingCalendar,
  })),
);

const DividendsCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.DividendsCalendar,
  })),
);

const EarningsCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.EarningsCalendar,
  })),
);

const ShortInterestCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.ShortInterestCalendar,
  })),
);

const InsiderTradesCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.InsiderTradesCalendar,
  })),
);

const EventsCalendar = React.lazy(() =>
  import('@benzinga/calendars').then(module => ({
    default: module.EventsCalendar,
  })),
);

export interface Props {
  calendar: CalendarTabSlugs;
  symbol: string;
  companyName?: string;
  initialData?: CalendarInitialDataTypes[];
  hideFilters?: boolean;
}

const QuoteCalendar: React.FC<Props> = ({ calendar, companyName, hideFilters, initialData, symbol }) => {
  let initialDateRange: { date_from: string; date_to: string } = { date_from: '', date_to: '' };
  let CalendarComponent: React.ComponentType<ICalendarProps> | null = null;
  const calendarData = Calendars[calendar];

  let hiddenColumns = ['ticker', 'name'];
  let noRowsOverlayText = '';

  switch (calendar) {
    case 'analyst-ratings': {
      CalendarComponent = AnalystRatingCalendar;
      if (calendarData.serverSide) {
        calendarData.serverSide.columnDef = AnalystRatingsColumnsDef;
      }
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      hiddenColumns = [...hiddenColumns, 'current_price', 'analyst_name'];
      break;
    }
    case 'events': {
      CalendarComponent = EventsCalendar;
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(addYears(new Date(), 2), 'yyyy-MM-dd'),
      };
      break;
    }
    case 'guidance': {
      CalendarComponent = GuidanceCalendar;
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      noRowsOverlayText = `${companyName} (${symbol}) does not provide guidance data`;
      break;
    }
    case 'dividends': {
      CalendarComponent = DividendsCalendar;
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      break;
    }
    case 'earnings': {
      CalendarComponent = EarningsCalendar;
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      break;
    }
    case 'short-interest': {
      CalendarComponent = ShortInterestCalendar;
      initialDateRange = {
        date_from: formatDate(subMonths(new Date(), 1), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      break;
    }
    case 'insider-trades': {
      CalendarComponent = InsiderTradesCalendar;
      initialDateRange = {
        date_from: formatDate(subYears(new Date(), 5), 'yyyy-MM-dd'),
        date_to: formatDate(new Date(), 'yyyy-MM-dd'),
      };
      break;
    }
    default: {
      CalendarComponent = null;
      break;
    }
  }

  // if (!initialData || !initialData.length) {
  //   return <div className="p-12 text-2xl text-center">No Data</div>;
  // }

  return (
    <div className="w-full lg:px-0 m-auto quote-calendar">
      <ErrorBoundary name="quote-calendar">
        {CalendarComponent !== null ? (
          <React.Suspense fallback={<div className="flex justify-center items-center h-64">Loading calendar...</div>}>
            <CalendarComponent
              calendar={calendar}
              calendarData={calendarData as CalendarDataI}
              calendarType={initialData?.length ? 'server_side' : undefined}
              hasStockTableStyling={true}
              height={!initialData ? 300 : 600}
              hiddenColumns={hiddenColumns}
              hideFilters={hideFilters}
              initialData={initialData || null}
              initialDateRange={initialDateRange}
              initialTicker={symbol}
              noRowsOverlayText={noRowsOverlayText}
            />
          </React.Suspense>
        ) : (
          <></>
        )}
      </ErrorBoundary>
    </div>
  );
};

export default QuoteCalendar;
